// PageItemModal.jsx

import React, { useMemo } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { getNodeTypeLabelFromDefinition } from 'utilities/NodeTypeLabels';

// CSS
import './PageItemModal.css';

// Components
import FieldBlockValue from './fieldvalue/FieldBlockValue';
import {
	buildFieldBlockRows,
	normalizeFieldBlockFieldKeys,
} from './layoutFieldDisplay';
import {
	PAGE_NODE_PRESENTATION,
} from './pageNodePresentation';
import PageNodePreview from './PageNodePreview';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';

const HIDDEN_FIELD_KEYS = new Set(['cover', 'coverUrl', 'title']);
const FALLBACK_FIELD_KEYS = ['dateModified'];

/**
 * resolvePageItemModalFieldKeys
 *
 * Resolves the field keys that should appear in the modal field list.
 *
 * @param {object|null} view - Source view definition for the clicked item.
 * @param {object|null} view - Source view definition for the clicked item.
 * @param {object|null} view - Source view definition for the clicked item.
 * @returns {array} The ordered field keys to render.
 */
function resolvePageItemModalFieldKeys(view) {
	const configuredFieldKeys = normalizeFieldBlockFieldKeys(view?.properties)
		.filter((fieldKey) => !HIDDEN_FIELD_KEYS.has(fieldKey));

	return configuredFieldKeys.length ? configuredFieldKeys : FALLBACK_FIELD_KEYS;
}

/**
 * PageItemModal
 *
 * Renders the shared read-oriented modal used for page item clicks.
 *
 * @param {function|null} [getTypeOverride=null] - Optional node type lookup override.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {object|null} node - Clicked node row.
 * @param {object|null} [nodesByKey=null] - Optional node lookup map for related field formatting.
 * @param {function} onClose - Called when the modal should close.
 * @param {object|null} [typeDef=null] - Optional resolved type definition for the clicked node.
 * @param {object|null} [view=null] - Optional source view definition used to derive field rows.
 * @returns {ReactNode} The rendered page item modal.
 */
const PageItemModal = ({
	getTypeOverride = null,
	isOpen,
	node = null,
	nodesByKey = null,
	onClose,
	typeDef = null,
	view = null,
}) => {

	// ----- DEVICE DETECTION ----- //

	const isMobile = useIsMobile();

	// ----- MEMOS ----- //

	const resolvedTypeDef = useMemo(() => {
		if (typeDef) return typeDef;
		if (typeof getTypeOverride !== 'function') return null;
		return getTypeOverride(String(node?.type || '').trim()) || null;
	}, [getTypeOverride, node?.type, typeDef]);

	const fieldRows = useMemo(() => {
		if (!node || !resolvedTypeDef) return [];

		return buildFieldBlockRows({
			fieldKeys: resolvePageItemModalFieldKeys(view),
			item: node,
			nodesByKey,
			typeDef: resolvedTypeDef,
		});
	}, [node, nodesByKey, resolvedTypeDef, view]);

	const subtitle = getNodeSubtitle(node).trim();
	const title = String(node?.title || '').trim() || 'Untitled';
	const typeKey = String(node?.type || '').trim().toUpperCase();
	const typeLabel = getNodeTypeLabelFromDefinition(resolvedTypeDef, node?.type, { fallback: 'Item' });
	const shouldShowPreview = typeKey === 'PHOTO' || typeKey === 'VIDEO';

	if (!node) return null;

	// ----- RENDER ----- //

	return (

		<Modal
			isOpen={isOpen}
			title={title}
			showHeader={false}
			enableClose={true}
			width={isMobile ? '100%' : 520}
			onClose={onClose}
		>
			<ModalForm>

				{/* TITLE */}
				<div className="page-item-modal-title">
					{title}
				</div>

				{/* DESCRIPTION */}
				{subtitle ? (
					<div className="page-item-modal-description">
						{subtitle}
					</div>
				) : null}

				{shouldShowPreview ? (
					<>

						{/* TYPE LABEL */}
						<div
							className="page-item-modal-type-label"
						>
							{typeLabel}
						</div>

						{/* PREVIEW */}
						<div className="page-item-modal-preview">
							<PageNodePreview
								getTypeOverride={getTypeOverride}
								node={node}
								presentation={PAGE_NODE_PRESENTATION.GALLERY}
								typeDefOverride={resolvedTypeDef}
							/>
						</div>
					</>
				) : null}

				{fieldRows.length ? (

					<div className="page-item-modal-fields">

						{fieldRows.map((row) => (

							<ModalField key={row.fieldKey}>

								<ModalLabel label={row.label} />

								{/* FIELD VALUE */}
								<div
									className="page-item-modal-field-value"
								>
									<FieldBlockValue
										node={node}
										row={row}
									/>
								</div>

							</ModalField>

						))}
					</div>

				) : null}

			</ModalForm>

			<ModalButtons>
				<Button
					iconKey="close"
					label="Close"
					onClick={onClose}
				/>
			</ModalButtons>

		</Modal>
	);
};

// Dev Tools
PageItemModal.displayName = 'PageItemModal';

export default PageItemModal;
